import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nurion/ui/select";

import { EmojiPicker } from "~/components/EmojiPicker";
import { usePortalRoot } from "~/components/EmbedPortalContext";
import { isEmoji, type UserStatus } from "~/hooks/useMyStatus";
import { resolveEmojiSuggestions, type Suggestion } from "~/lib/autocomplete-providers";
import { replaceShortcodes, splitLeadingShortcode } from "~/lib/emoji-shortcodes";
import {
  getPortalViewportSize,
  resolveAnchorPosition,
  type AnchorPlacement,
  type PortalSurface,
} from "~/lib/portal-positioning";
import { useUnderline } from "~/lib/underline";

type ExpiryValue = "30m" | "1h" | "2h" | "4h" | "today" | "tomorrow" | "custom" | "never";

interface Preset {
  emoji: string;
  label: string;
  expiry: ExpiryValue;
}

const PRESETS: Preset[] = [
  { emoji: "🗓️", label: "In a meeting", expiry: "30m" },
  { emoji: "🍽️", label: "Lunch", expiry: "1h" },
  { emoji: "🎧", label: "Heads down", expiry: "2h" },
  { emoji: "🤒", label: "Sick", expiry: "today" },
  { emoji: "🏝️", label: "Vacation", expiry: "tomorrow" },
  { emoji: "💼", label: "Working remotely", expiry: "today" },
];

const PRESET_EXPIRY_LABEL: Record<ExpiryValue, string> = {
  "30m": "30 min",
  "1h": "1 hour",
  "2h": "2 hours",
  "4h": "4 hours",
  today: "Today",
  tomorrow: "Until tomorrow",
  custom: "",
  never: "",
};

const STATUS_PICKER_MIN_WIDTH = 288;
const STATUS_PICKER_MAX_WIDTH = 320;
const STATUS_PICKER_TRIGGER_EXTRA_WIDTH = 96;
const STATUS_PICKER_MARGIN = 8;

function endOfToday(): number {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function endOfTomorrow(): number {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function toLocalDateTime(value: number): string {
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function initialExpiry(status: UserStatus | null): ExpiryValue {
  return status?.expiresAt ? "custom" : "never";
}

export function expiryMs(value: ExpiryValue, custom: string): number | null | undefined {
  const now = Date.now();
  if (value === "30m") return now + 30 * 60_000;
  if (value === "1h") return now + 60 * 60_000;
  if (value === "2h") return now + 2 * 60 * 60_000;
  if (value === "4h") return now + 4 * 60 * 60_000;
  if (value === "today") return endOfToday();
  if (value === "tomorrow") return endOfTomorrow();
  if (value === "custom") {
    const parsed = new Date(custom).getTime();
    if (!Number.isFinite(parsed) || parsed <= now) return undefined;
    return parsed;
  }
  return null;
}

export function StatusPicker({
  anchor,
  status,
  onSetStatus,
  onClear,
  onClose,
  labelledBy,
}: {
  anchor: HTMLElement | null;
  status: UserStatus | null;
  onSetStatus: (status: UserStatus) => Promise<void> | void;
  onClear: () => Promise<void> | void;
  onClose: () => void;
  labelledBy?: string;
}) {
  const portalRoot = usePortalRoot();
  const [emoji, setEmoji] = useState(status?.emoji ?? "🍽️");
  const [message, setMessage] = useState(status?.message ?? "");
  const [expiry, setExpiry] = useState<ExpiryValue>(initialExpiry(status));
  const [custom, setCustom] = useState(status?.expiresAt ? toLocalDateTime(status.expiresAt) : "");
  const [error, setError] = useState<string | null>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const emojiButtonRef = useRef<HTMLButtonElement | null>(null);
  const messageInputRef = useRef<HTMLInputElement | null>(null);
  const didInitialFocus = useRef(false);
  const messageUnderline = useUnderline();
  const customUnderline = useUnderline();
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const [placement, setPlacement] = useState<AnchorPlacement | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return;
    const computeViewportCoords = (rect: DOMRect, surface: PortalSurface) => {
      const availableWidth = Math.max(0, surface.width - STATUS_PICKER_MARGIN * 2);
      const nextWidth = Math.min(
        STATUS_PICKER_MAX_WIDTH,
        availableWidth,
        Math.max(STATUS_PICKER_MIN_WIDTH, rect.width + STATUS_PICKER_TRIGGER_EXTRA_WIDTH),
      );
      setWidth(nextWidth);
      const height = popoverRef.current?.getBoundingClientRect().height ?? 312;
      const viewportLeft = Math.min(
        Math.max(surface.offsetLeft + STATUS_PICKER_MARGIN, rect.left),
        surface.offsetLeft + surface.width - nextWidth - STATUS_PICKER_MARGIN,
      );
      const topAbove = rect.top - height - STATUS_PICKER_MARGIN;
      const topBelow = rect.bottom + STATUS_PICKER_MARGIN;
      const minTop = surface.offsetTop + STATUS_PICKER_MARGIN;
      const maxTop = surface.offsetTop + surface.height - height - STATUS_PICKER_MARGIN;
      const viewportTop =
        topAbove >= minTop ? topAbove : Math.max(minTop, Math.min(topBelow, maxTop));
      return { viewportTop, viewportLeft };
    };
    const update = () => {
      const surface = getPortalViewportSize(portalRoot);
      const { viewportTop, viewportLeft } = computeViewportCoords(
        anchor.getBoundingClientRect(),
        surface,
      );
      setPlacement(resolveAnchorPosition({ portalRoot, viewportTop, viewportLeft }));
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor, portalRoot, measuredHeight]);

  useLayoutEffect(() => {
    const height = popoverRef.current?.getBoundingClientRect().height ?? 0;
    if (height > 0 && height !== measuredHeight) setMeasuredHeight(height);
  }, [placement, measuredHeight]);

  const hasPlacement = placement !== null;
  useEffect(() => {
    const el = popoverRef.current;
    if (!hasPlacement || !el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const height = el.getBoundingClientRect().height;
      if (height > 0) setMeasuredHeight(height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasPlacement]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (event.defaultPrevented) return;
      onClose();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (popoverRef.current?.contains(target)) return;
      if (anchor?.contains(target)) return;
      const element = target instanceof Element ? target : target.parentElement;
      if (element?.closest('[data-slot="select-content"]')) return;
      if (element?.closest("[data-chat-emoji-picker]")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown);
    };
  }, [anchor, onClose]);

  useEffect(() => {
    if (didInitialFocus.current) return;
    if (!popoverRef.current) return;
    didInitialFocus.current = true;
    messageInputRef.current?.focus();
  }, [placement]);

  useEffect(() => {
    const match = /(?:^|\s):([a-z0-9_+-]{2,})$/i.exec(message);
    if (!match?.[1]) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    void resolveEmojiSuggestions({ query: match[1], maxResults: 6 })
      .fetch()
      .then((next) => {
        if (!cancelled) setSuggestions(next);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [message]);

  const pickSuggestion = (native: string) => {
    const stripped = message.replace(/:([a-z0-9_+-]{2,})$/i, "");
    if (stripped.trim().length === 0) {
      setEmoji(native);
      setMessage("");
    } else {
      setMessage(`${stripped}${native}`);
    }
    setSuggestions([]);
    messageInputRef.current?.focus();
  };

  const save = async () => {
    const cleanEmoji = emoji.trim();
    const cleanMessage = message.trim();
    const expiresAt = expiryMs(expiry, custom);
    if (!cleanEmoji || !cleanMessage) {
      setError("Add an emoji and a message.");
      return;
    }
    if (!isEmoji(cleanEmoji)) {
      setError("Use a single emoji.");
      return;
    }
    if (expiresAt === undefined) {
      setError("Choose a future custom time.");
      return;
    }
    setError(null);
    try {
      await onSetStatus({ emoji: cleanEmoji, message: cleanMessage, expiresAt });
      onClose();
    } catch (err) {
      console.warn("set status failed", err);
      setError("Could not save status.");
    }
  };

  const applyPreset = async (preset: Preset) => {
    try {
      await onSetStatus({
        emoji: preset.emoji,
        message: preset.label,
        expiresAt: expiryMs(preset.expiry, custom) ?? null,
      });
      onClose();
    } catch (err) {
      console.warn("set status preset failed", err);
      setError("Could not save status.");
    }
  };

  if (!anchor || !portalRoot || !placement || width == null) return null;

  const content = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Set status"
      aria-labelledby={labelledBy}
      style={{
        position: placement.position,
        top: placement.top,
        left: placement.left,
        zIndex: 70,
        width,
        padding: "var(--chat-space-4)",
        borderRadius: "var(--chat-radius-xl)",
        background: "hsl(var(--background))",
        border: "1px solid var(--chat-surface-border)",
        boxShadow: "var(--chat-shadow-popover)",
        color: "var(--chat-fg-primary)",
      }}
    >
      <div role="group" aria-label="Status presets" style={{ display: "grid", gap: 2 }}>
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => void applyPreset(preset)}
            className="chat-button hover:bg-[var(--chat-surface-raised)]"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-3)",
              width: "100%",
              height: 32,
              padding: "0 var(--chat-space-2)",
              border: "none",
              borderRadius: "var(--chat-radius-md)",
              background: "transparent",
              color: "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-sm-size)",
              lineHeight: "var(--chat-type-body-sm-line)",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <span
              aria-hidden
              style={{ width: 18, fontSize: 16, lineHeight: "18px", textAlign: "center" }}
            >
              {preset.emoji}
            </span>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {preset.label}
            </span>
            <span
              style={{ color: "var(--chat-fg-muted)", fontSize: "var(--chat-type-body-xs-size)" }}
            >
              {PRESET_EXPIRY_LABEL[preset.expiry]}
            </span>
          </button>
        ))}
      </div>

      <div
        style={{ marginTop: "var(--chat-space-5)", display: "grid", gap: "var(--chat-space-3)" }}
      >
        <span
          style={{
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          Custom
        </span>
        <div
          style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: "var(--chat-space-2)" }}
        >
          <button
            ref={emojiButtonRef}
            type="button"
            aria-label={`Status emoji ${emoji}. Change emoji`}
            aria-haspopup="dialog"
            aria-expanded={emojiPickerOpen}
            onClick={() => setEmojiPickerOpen((open) => !open)}
            className="chat-button hover:bg-[var(--chat-surface-raised)]"
            style={{
              width: 32,
              height: 32,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              border: "1px solid var(--chat-surface-border)",
              borderRadius: "var(--chat-radius-md)",
              background: "transparent",
              fontSize: 18,
              lineHeight: "18px",
              cursor: "pointer",
            }}
          >
            {emoji}
          </button>
          <label style={{ display: "grid", gap: 2 }}>
            <span className="sr-only">Message</span>
            <input
              ref={messageInputRef}
              aria-label="Message"
              value={message}
              placeholder="What's your status? Try :rocket:"
              aria-invalid={error ? true : undefined}
              onChange={(event) => {
                const raw = event.currentTarget.value;
                const lifted = splitLeadingShortcode(raw);
                if (lifted) {
                  setEmoji(lifted.emoji);
                  setMessage(replaceShortcodes(lifted.rest));
                  return;
                }
                setMessage(replaceShortcodes(raw));
              }}
              onFocus={messageUnderline.onFocus}
              onBlur={messageUnderline.onBlur}
              style={inputStyle("left", messageUnderline.borderColor)}
            />
          </label>
        </div>
        {emojiPickerOpen ? (
          <EmojiPicker
            anchor={emojiButtonRef.current}
            onSelect={(native) => {
              setEmoji(native);
              setEmojiPickerOpen(false);
              emojiButtonRef.current?.focus();
            }}
            onClose={() => setEmojiPickerOpen(false)}
          />
        ) : null}
        {suggestions.length > 0 ? (
          <div
            role="listbox"
            aria-label="Emoji suggestions"
            style={{ display: "flex", flexWrap: "wrap", gap: "var(--chat-space-1)" }}
          >
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                type="button"
                role="option"
                aria-selected={false}
                aria-label={suggestion.secondary ?? suggestion.primary}
                title={suggestion.secondary ?? suggestion.primary}
                onClick={() => pickSuggestion(suggestion.replacement)}
                className="chat-button hover:bg-[var(--chat-surface-raised)]"
                style={{
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: "none",
                  borderRadius: "var(--chat-radius-md)",
                  background: "transparent",
                  fontSize: 18,
                  lineHeight: "18px",
                  cursor: "pointer",
                }}
              >
                {suggestion.replacement}
              </button>
            ))}
          </div>
        ) : null}
        <div style={{ display: "grid", gap: "var(--chat-space-1)" }}>
          <span style={labelStyle}>Expiry</span>
          <Select value={expiry} onValueChange={(value) => setExpiry(value as ExpiryValue)}>
            <SelectTrigger aria-label="Expiry" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              position="popper"
              side="top"
              collisionPadding={8}
              className="z-[80] bg-[var(--chat-surface-overlay)] text-[var(--chat-fg-primary)] backdrop-blur-none"
              portalContainer={portalRoot}
            >
              <SelectItem value="30m">30m</SelectItem>
              <SelectItem value="1h">1h</SelectItem>
              <SelectItem value="2h">2h</SelectItem>
              <SelectItem value="4h">4h</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="tomorrow">Tomorrow</SelectItem>
              <SelectItem value="custom">Custom time</SelectItem>
              <SelectItem value="never">Never</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {expiry === "custom" ? (
          <input
            aria-label="Custom time"
            type="datetime-local"
            value={custom}
            aria-invalid={error ? true : undefined}
            onChange={(event) => setCustom(event.currentTarget.value)}
            onFocus={customUnderline.onFocus}
            onBlur={customUnderline.onBlur}
            style={inputStyle("left", customUnderline.borderColor)}
          />
        ) : null}
        {error ? <p style={errorStyle}>{error}</p> : null}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button
            type="button"
            className="chat-button"
            disabled={!emoji.trim() || !message.trim()}
            onClick={() => void save()}
            style={saveButtonStyle}
          >
            Save status
          </button>
          {status ? (
            <button
              type="button"
              className="chat-button"
              onClick={() => {
                void Promise.resolve(onClear())
                  .then(onClose)
                  .catch((err) => {
                    console.warn("clear status failed", err);
                    setError("Could not clear status.");
                  });
              }}
              style={clearButtonStyle}
            >
              Clear status
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
  return createPortal(content, portalRoot);
}

const labelStyle = {
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
} satisfies CSSProperties;

const errorStyle = {
  margin: 0,
  color: "var(--chat-state-danger)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
} satisfies CSSProperties;

function inputStyle(textAlign: "left" | "center", borderColor: string): CSSProperties {
  return {
    width: "100%",
    minWidth: 0,
    height: 32,
    border: "none",
    borderBottom: `1px solid ${borderColor}`,
    borderRadius: 0,
    background: "transparent",
    color: "var(--chat-fg-primary)",
    fontFamily: "var(--chat-font-sans)",
    fontSize: "var(--chat-type-body-md-size)",
    lineHeight: "var(--chat-type-body-md-line)",
    outline: "none",
    textAlign,
    transition: "border-color var(--chat-motion-fast) var(--chat-motion-ease)",
  };
}

const saveButtonStyle = {
  height: 32,
  padding: "0 var(--chat-space-3)",
  border: "none",
  borderRadius: "var(--chat-radius-sm)",
  background: "var(--chat-cta-bg)",
  color: "var(--chat-cta-fg)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
} satisfies CSSProperties;

const clearButtonStyle = {
  height: 32,
  padding: "0 var(--chat-space-3)",
  border: "none",
  borderRadius: "var(--chat-radius-sm)",
  background: "transparent",
  color: "var(--chat-state-danger)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
} satisfies CSSProperties;
